import { messagesNow } from '@/i18n'
import { messages } from './messages'

// Деньги приходят строками ("125.0000") и строками остаются. Number здесь —
// это округление реальных денег автора, поэтому сравнение, показ и
// арифметика сделаны на строках. Значения неотрицательные: балансы и цены
// прогонов.

const split = (value: string): [string, string] => {
  const [int = '0', frac = ''] = value.trim().split('.')
  // подставляем '0' явно при пустой целой части (дефолт деструктуризации не работает на пустую строку)
  const intPart = (int || '0').replace(/^0+(?=\d)/, '')
  // ведущие нули убираем, но последний разряд оставляем: '000' -> '0'
  return [intPart, frac]
}

/** Хватает ли баланса на прогон. Сравнение по разрядам, не лексикографическое. */
export const hasEnough = (balance: string, required: string): boolean => {
  const [bInt, bFrac] = split(balance)
  const [rInt, rFrac] = split(required)
  if (bInt.length !== rInt.length) return bInt.length > rInt.length
  const width = Math.max(bFrac.length, rFrac.length)
  return bInt + bFrac.padEnd(width, '0') >= rInt + rFrac.padEnd(width, '0')
}

/**
 * Сколько знаков после точки видит автор (решение заказчика): одна, с обычным
 * округлением — «47.3944» → «47.4», «72.0842» → «72.1». Округление на строках
 * (прибавить половину младшего разряда и отрезать), без `Number`. Считать
 * по-прежнему нужно по полной строке — это только показ. Целый баланс в
 * сайдбаре округляется ВНИЗ отдельно (`useCreditsDisplay`): там обещать лишнее
 * нельзя.
 */
const SHOWN_DECIMALS = 1
const HALF_STEP = `0.${'0'.repeat(SHOWN_DECIMALS)}5`

/** Значение для показа человеку: '125.0000' -> '125', '0010.5000' -> '10.5', '47.3944' -> '47.4'. */
export const formatCredits = (value: string): string => {
  const [intPart, frac] = split(addCredits(value, HALF_STEP))
  const meaningful = frac.slice(0, SHOWN_DECIMALS).replace(/0+$/, '')
  return meaningful ? `${intPart}.${meaningful}` : intPart
}

/**
 * Согласование слова «кредит» с суммой. Считается ПО СТРОКЕ, без `Number`:
 * правило склонения смотрит только на последние два разряда целой части, а
 * приведение денег к числу тут было бы округлением реальных денег автора.
 *
 * Дробная сумма — всегда родительный падеж единственного числа («1.5 кредита»,
 * «0.5 кредита»): по русской норме дробь управляет именно им, а не тем числом,
 * которое стоит в целой части.
 */
const creditsForm = (value: string): 'one' | 'few' | 'many' => {
  const [intPart, frac] = split(value)
  if (frac.replace(/0+$/, '')) return 'few'
  const tens = intPart.slice(-2, -1)
  const ones = intPart.slice(-1)
  // 11-14 — исключение из правила единиц: «11 кредитов», а не «11 кредит».
  if (tens === '1') return 'many'
  if (ones === '1') return 'one'
  return ones === '2' || ones === '3' || ones === '4' ? 'few' : 'many'
}

/** Сумма со согласованным словом: «1 кредит», «2 кредита», «11 кредитов».
 *  Единственный способ напечатать кредиты словом — «Списано 1 кредитов»
 *  ловили в трёх местах разом, потому что склонение писалось руками. */
export const formatCreditsWithWord = (value: string): string =>
  // Слово согласуем с ПОКАЗАННЫМ числом: «84.004» печатается как «84», и
  // форма должна быть «84 кредита», а не дробная.
  messagesNow(messages).creditsWithWord(formatCredits(value), creditsForm(formatCredits(value)))

// Поразрядное сложение двух выровненных строк цифр (без точки, без знака).
// Возвращает сумму той же длины либо на один разряд длиннее (перенос в старший).
const addDigitStrings = (a: string, b: string): string => {
  let carry = 0
  let out = ''
  for (let i = a.length - 1; i >= 0; i--) {
    const sum = a.charCodeAt(i) - 48 + (b.charCodeAt(i) - 48) + carry
    carry = sum >= 10 ? 1 : 0
    out = String(sum % 10) + out
  }
  return carry ? `1${out}` : out
}

/** Сложение двух денежных строк поразрядно, без перевода в число. */
export const addCredits = (a: string, b: string): string => {
  const [aInt, aFrac] = split(a)
  const [bInt, bFrac] = split(b)
  const fracWidth = Math.max(aFrac.length, bFrac.length)
  const intWidth = Math.max(aInt.length, bInt.length)
  const digitsA = aInt.padStart(intWidth, '0') + aFrac.padEnd(fracWidth, '0')
  const digitsB = bInt.padStart(intWidth, '0') + bFrac.padEnd(fracWidth, '0')
  const sum = addDigitStrings(digitsA, digitsB)
  const intLen = sum.length - fracWidth
  const intPart = sum.slice(0, intLen) || '0'
  const fracPart = fracWidth ? sum.slice(intLen) : ''
  return fracPart ? `${intPart}.${fracPart}` : intPart
}

/**
 * Умножение денежной строки на неотрицательное целое количество штук
 * (блоков, картинок — НЕ деньги, поэтому `count` остаётся обычным числом).
 * Реализовано сложением, без перевода цены в число.
 */
export const multiplyCredits = (price: string, count: number): string => {
  const [, frac] = split(price)
  if (count === 0) return frac ? `0.${'0'.repeat(frac.length)}` : '0'
  let acc = price
  for (let i = 1; i < count; i += 1) acc = addCredits(acc, price)
  return acc
}

/**
 * Строго больше нуля для неотрицательной денежной строки: «есть хоть одна
 * ненулевая цифра». `Number` к деньгам в этом репозитории не применяется, а
 * `parseFloat('0.0000') > 0` — ровно то округление, от которого весь файл и
 * защищает.
 *
 * Принимает `null`/`undefined` намеренно: и цена пакета (`priceRub`), и
 * заморозка недоехавшего баланса приходят отсутствующими, и каждый
 * вызывающий иначе писал бы свою защиту.
 */
export const isPositiveCredits = (value: string | null | undefined): boolean =>
  Boolean(value) && /[1-9]/.test(value ?? '')

/**
 * Целая часть суммы — округление ВНИЗ, без `Number`. Нужна витринам, где
 * важнее спокойный вид, чем точность до тысячных: «731.557 кредита» в плашке
 * сайдбара переносится на две строки и читается как ошибка.
 *
 * Вниз, а не к ближайшему, сознательно: округление вверх обещало бы автору
 * деньги, которых у него нет. Точная сумма при этом никуда не девается — она
 * в окне кредитов, куда плашка и ведёт.
 */
export const floorCredits = (value: string): string => split(value)[0]

/**
 * Сумма из ответа сервера, которой может не быть. `formatCredits` на
 * `undefined` БРОСАЕТ (`split` зовёт `.trim()`), а окно кредитов глобальное и
 * живёт вне единственного `ErrorBoundary` — одна неполная строка истории
 * унесла бы всё приложение.
 *
 * Отдельная функция, а не защита внутри `formatCredits`: там, где сумма
 * обязана быть (цена прогона, номинал пакета), молчаливый прочерк спрятал бы
 * настоящую поломку контракта.
 */
export const formatCreditsOrDash = (value: string | null | undefined): string =>
  value ? formatCredits(value) : '—'
